/**
 * Day-cell severity — maps ONE bucket of the uptime bar to a colour band.
 * This is the SINGLE SOURCE OF TRUTH for how a cell of the 90d bar is
 * coloured. Tweak the thresholds here and every bar follows.
 *
 * Why a ladder at all. The backend colours a bucket by `downCount > 0`
 * (`backend/src/modules/status/uptime-stats.util.ts`), which is a test for a
 * non-empty set, not a measurement. At 141 120 probes/day (98 endpoints × 1/min)
 * the probability that *something* blinked once in a day is ≈ 1, so red became
 * the default state and stopped carrying information: on 2026-09-17 API v2
 * mainnet showed 56 red days out of 90, and 33 of them were labelled "100.00%".
 *
 * Where the numbers come from:
 *   99.9% — 1 min 26 s of a day (Google SRE availability table).
 *   99%   — 14 min 24 s of a day.
 *   95%   — Atlassian Statuspage turns a day fully red at 60 weighted minutes
 *           of outage, i.e. 95.83% of a day; it is also the `operational`
 *           floor of our own suite pill in `suite-severity.ts`, which removes
 *           the old contradiction of a green suite sitting over red days.
 *
 * Scope. The ladder answers "how bad was this DAY", so it applies to the 90d
 * window only. The 24h and 1h windows keep the binary rule: they are the
 * operator's view, where a single failed run must be visible immediately.
 *
 * (The suite-level pill keeps its own, looser ladder in `suite-severity.ts` —
 * "how healthy is the suite overall" is a different question, and the two must
 * not be collapsed into one.)
 */
import type { BucketStatus, UptimeWindow } from './status-types';

/** `BucketStatus` plus the orange band the day ladder can produce. */
export type BucketSeverity = BucketStatus | 'major';

/**
 * Lower bounds (INCLUSIVE) per band, as success FRACTIONS (0..1). The upper
 * bound of each band is the next-higher band's lower bound (exclusive), so
 * 99.9% is ok and 99.89% is warn.
 *
 * NB: fractions, not percentages — the API returns success as 0..1.
 */
export const DAY_SEVERITY_THRESHOLDS = {
  /** ≥ 99.9% of probes passed (≤ 1 min 26 s equivalent downtime) → ok. */
  ok: 0.999,
  /** ≥ 99% (≤ 14 min 24 s) → warn (amber). */
  warn: 0.99,
  /** ≥ 95% (≤ 72 min) → major (orange). */
  major: 0.95,
  // < 95% → down (red) — the implicit floor band.
} as const;

/**
 * The bucket fields the ladder reads. Structurally satisfied by both
 * `WindowBucket` and `EndpointSummary`, so one function colours the day cells
 * and the endpoint-row dots alike.
 */
export interface SeverityInput {
  status: BucketStatus;
  warnCount: number;
  downCount: number;
  totalProbes: number;
  successPct: number;
}

/**
 * Colour band for one bucket.
 *
 * For 24h/1h the backend's own verdict passes through untouched, so those
 * windows keep byte-identical behaviour. For 90d the ladder applies, with two
 * guards:
 *
 *   - an empty bucket is `no_data`, never a percentage (0% success over 0
 *     probes is not an outage);
 *   - `skipped` probes are not failures. They drag `successPct` down because
 *     they miss the numerator, but with no real failure the cell can never be
 *     worse than `warn` — otherwise a day of skips would read as an outage.
 */
export function bucketSeverity(
  bucket: SeverityInput,
  windowSel: UptimeWindow,
): BucketSeverity {
  if (windowSel !== '90d') return bucket.status;
  if (bucket.totalProbes === 0) return 'no_data';
  // Defensive: a malformed percentage must not silently land in a band.
  if (!Number.isFinite(bucket.successPct)) return bucket.status;

  const pct = bucket.successPct;
  const severity: BucketSeverity =
    pct >= DAY_SEVERITY_THRESHOLDS.ok
      ? 'ok'
      : pct >= DAY_SEVERITY_THRESHOLDS.warn
        ? 'warn'
        : pct >= DAY_SEVERITY_THRESHOLDS.major
          ? 'major'
          : 'down';

  if (bucket.downCount === 0 && (severity === 'major' || severity === 'down')) {
    return 'warn';
  }
  return severity;
}

/**
 * Whether the cell carries the "there were failures here" stripe.
 *
 * The ladder deliberately lets a day with a handful of failures render green —
 * that is the point. The stripe is what keeps it honest: the colour calms
 * down, the evidence stays. Only `ok` cells get it; on warn/major/down the
 * colour already says it, and on `no_data` there is nothing to report.
 *
 * Kept as a pure predicate rather than inline JSX because this repo does not
 * test the DOM — see the note at the top of `IndexerPipelineCard.test.tsx`.
 */
export function shouldMarkFailures(
  bucket: Pick<SeverityInput, 'downCount'>,
  severity: BucketSeverity,
): boolean {
  return severity === 'ok' && bucket.downCount > 0;
}
